import { EmailDraft, EmailLog } from '../models/types';
import { v4 as uuidv4 } from 'uuid';
import { logger, LogCategory } from '../../../services/logger';

/**
 * Service for logging email generation events
 */
export class LoggingService {
  private readonly STORAGE_KEY = 'email_logs';
  
  /**
   * Logs an email generation event
   * @param emailDraft The email draft
   * @param userId The ID of the user
   * @param action The action performed (default: 'create')
   * @returns The ID of the log entry
   */
  public logEmailGeneration(emailDraft: EmailDraft, userId: string, action = 'create'): string {
    const log: EmailLog = {
      id: uuidv4(),
      emailDraftId: emailDraft.id,
      action,
      timestamp: new Date(),
      userId,
      details: `${action} email draft for ${emailDraft.meetingInfo.title}`
    };
    
    const logs = this.getAllLogs();
    logs.push(log);
    this.saveLogsToStorage(logs);
    
    return log.id;
  }
  
  /**
   * Retrieves logs from LocalStorage
   * @param filters Optional filters to apply
   * @returns Array of filtered log entries
   */
  public getLogs(filters: Partial<EmailLog> = {}): EmailLog[] {
    const logs = this.getAllLogs();
    
    // Apply filters if provided
    return logs.filter(log => {
      for (const [key, value] of Object.entries(filters)) {
        if (log[key as keyof EmailLog] !== value) {
          return false;
        }
      }
      return true;
    });
  }
  
  /**
   * Retrieves logs for a specific email draft
   * @param emailDraftId The ID of the email draft
   * @returns Array of log entries for the email draft
   */
  public getLogsForEmailDraft(emailDraftId: string): EmailLog[] {
    return this.getLogs({ emailDraftId });
  }
  
  /**
   * Retrieves logs for a specific user
   * @param userId The ID of the user
   * @returns Array of log entries for the user
   */
  public getLogsForUser(userId: string): EmailLog[] {
    return this.getLogs({ userId });
  }
  
  /**
   * Retrieves all logs from LocalStorage
   * @returns Array of log entries
   */
  private getAllLogs(): EmailLog[] {
    try {
      const logsJson = localStorage.getItem(this.STORAGE_KEY);
      
      if (!logsJson) {
        return [];
      }
      
      const logs = JSON.parse(logsJson);
      
      // Convert string dates back to Date objects
      return logs.map((log: any) => ({
        ...log,
        timestamp: new Date(log.timestamp)
      }));
    } catch (error) {
      logger.error('Error retrieving logs from LocalStorage', LogCategory.STORAGE, { error });
      return [];
    }
  }
  
  /**
   * Saves logs to LocalStorage
   * @param logs The logs to save
   */
  private saveLogsToStorage(logs: EmailLog[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
      logger.error('Error saving logs to LocalStorage', LogCategory.STORAGE, { error });
    }
  }
  
  /**
   * Clears all logs
   */
  public clearLogs(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
}